import { Button } from "@/components/ui/button";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useFacilitiesContext } from "@/hooks/useFacilitiesContext";
import { useUnitItemContext } from "@/hooks/useUnitItemContext";
import { useUnitJourneyContext } from "@/hooks/useUnitJourneyContext";
import { translateWithMultipleSources } from "@/lib/translate";
import { FunnelType } from "@/lib/utils";

import { emitUnitEvent } from "@/utils/units/unitEvents";
import React from "react";

interface UnitReserveButtonProps {
    unitId: number | string;
    className?: string;
}

export const UnitReserveButton: React.FC<UnitReserveButtonProps> = ({
    unitId,
    className = "reserve-btn"
}) => {
    const { reservationApp, disableReservation, language } =
        useDisplayOptionContext();
    const unitContext = useUnitItemContext();
    const journeyContext = useUnitJourneyContext();
    const { facilities } = useFacilitiesContext();

    const { unitGroup, selectedPromo } = unitContext;

    const handleReserveEvent = () => {
        emitUnitEvent(
            unitId,
            unitGroup.representative_unit.facility_id,
            selectedPromo,
            "reservation",
        );
    };

    const handleExternalReservation = () => {
        const facilityId = unitGroup.representative_unit.facility_id;
        const facilityInfo = facilities.find(
            (facility) => facility.id === facilityId,
        );

        if (facilityInfo && reservationApp?.externalUrl) {
            const translatedUrl = translateWithMultipleSources(
                reservationApp.externalUrl,
                {
                    unitGroup,
                    facility: facilityInfo,
                },
                Number(unitId),
                Number(selectedPromo),
            );
            window.location.href = translatedUrl;
        }
    };

    const activateReservationJourney = () => {
        if (journeyContext && !journeyContext.isOpen) {
            journeyContext.setIsOpen(true);
        }
        journeyContext?.setSelectedUnitId(unitId);
        journeyContext?.setUnitJourneyStep("funnel");
        journeyContext?.setFunnelType(FunnelType.RESERVE);
    };

    const handleReserveClick = () => {
        handleReserveEvent();

        if (
            reservationApp?.location === "external" &&
            reservationApp.externalUrl
        ) {
            handleExternalReservation();
        } else {
            activateReservationJourney();
        }
    };

    if (disableReservation) return null;

    return (
        <Button
            variant="secondary"
            data-testid="reserveButton"
            onClick={handleReserveClick}
            className={className}
        >
            {language?.action?.reserveButton}
        </Button>
    );
};
